import React, { useEffect, useState } from 'react';
import { Toaster, toast } from 'sonner';
import { ArrowRight, Plus, Leaf, BookOpen, ScrollText, Users, Printer, Download, X, Eye } from 'lucide-react';
import { CreatorLinks } from './components/CreatorLinks';
import { TrayGrid } from './components/TrayGrid';
import { SpreadCanvas } from './components/SpreadCanvas';
import { TendingBench } from './components/TendingBench';
import { LedgerPanel } from './components/LedgerPanel';
import { EmotionWheel, GRANULAR_EMOTIONS } from './components/EmotionWheel';
import { CognitionLens } from './components/CognitionLens';
import {
  getMyTrays, createTrayLocal, joinTrayLocal, setCurrentTray, getCurrentTray,
  plantSeedling, rememberTray, forgetTray, apiCreateTray, apiJoinTray,
} from './lib/store';
import type { LocalTrayState, Seedling } from './lib/store';
import { generateId } from './lib/ids';
import { saveAs } from 'file-saver';

type View = 'landing' | 'tray';

export default function App() {
  const [view, setView] = useState<View>('landing');
  const [state, setState] = useState<LocalTrayState | null>(null);
  const [activeBench, setActiveBench] = useState<Seedling | null>(null);
  const [tab, setTab] = useState<'grid' | 'wheel' | 'spread' | 'ledger'>('grid');
  const [wheelFilter, setWheelFilter] = useState<string | null>(null);
  const [myTrays, setMyTrays] = useState(() => getMyTrays());
  const [joinCode, setJoinCode] = useState('');
  const [newTendril, setNewTendril] = useState('');
  const [trayName, setTrayName] = useState('');
  const [liveConnected] = useState(false);

  // Rich plant form state (replaces crude window.prompt)
  const [plantingCell, setPlantingCell] = useState<number | null>(null);
  const [plantForm, setPlantForm] = useState({
    content: '',
    attention: '',
    species: '',
    repair_readiness: 50,
    intention: '',
    impact: '',
    accountability: '',
    followups: [{ actor: '', action: '', timeframe: '' }] as Array<{ actor: string; action: string; timeframe: string }>,
    emotion_tags: [] as string[],
  });

  // Load last tray on mount for delightful return
  useEffect(() => {
    const current = getCurrentTray();
    if (current) {
      setState(current);
      setView('tray');
    }
  }, []);

  function refreshMy() {
    setMyTrays(getMyTrays());
  }

  function openTray(s: LocalTrayState) {
    setState(s);
    setCurrentTray(s);
    setView('tray');
    setTab('grid');
    refreshMy();
  }

  async function createLive() {
    try {
      const res = await apiCreateTray(trayName || undefined);
      const s: LocalTrayState = {
        tray: res.tray,
        members: [res.myMember],
        seedlings: [],
        tendings: [],
        connections: [],
        aftertastes: [],
        myMemberId: res.myMember.id,
      };
      rememberTray({ trayId: res.tray.id, myMemberId: res.myMember.id, code: res.code });
      setState(s);
      setCurrentTray(s);
      setView('tray');
      toast.success(`Live tray ready. Code: ${res.code}`);
    } catch (e) {
      // graceful fallback to local demo
      const s = createTrayLocal(trayName || undefined);
      openTray(s);
      toast.info('Created locally (demo). Connect a worker for real shared trays.');
    }
  }

  async function joinLive() {
    if (!joinCode.trim()) return;
    const tendril = newTendril.trim() || 'a quiet tendril';
    try {
      const res = await apiJoinTray(joinCode.trim().toUpperCase(), tendril);
      const s: LocalTrayState = {
        tray: res.tray,
        members: [...(getCurrentTray()?.members || []), res.myMember],
        seedlings: [],
        tendings: [],
        connections: [],
        aftertastes: [],
        myMemberId: res.myMember.id,
      };
      rememberTray({ trayId: res.tray.id, myMemberId: res.myMember.id, code: res.tray.code });
      setState(s);
      setCurrentTray(s);
      setView('tray');
      setJoinCode(''); setNewTendril('');
      toast.success(`Joined as ${tendril}`);
    } catch {
      const s = joinTrayLocal(joinCode.trim(), tendril);
      openTray(s);
      setJoinCode(''); setNewTendril('');
      toast.info('Joined a local mirror of that code (demo).');
    }
  }

  function openPlant(cell: number) {
    // Open rich structured form for capturing full cognition asset
    const existing = state?.seedlings.find(s => s.cell_index === cell && !s.retracted_at);
    if (existing) {
      setActiveBench(existing);
      return;
    }
    setPlantingCell(cell);
    setPlantForm({
      content: '',
      attention: '',
      species: 'systems noticing',
      repair_readiness: 50,
      intention: '',
      impact: '',
      accountability: '',
      followups: [{ actor: '', action: '', timeframe: '' }],
      emotion_tags: [],
    });
  }

  function submitPlant() {
    if (!plantingCell || !plantForm.content.trim()) {
      setPlantingCell(null);
      return;
    }
    const cleanFollowups = plantForm.followups.filter(f => f.actor.trim() || f.action.trim());
    const s = plantSeedling(
      plantForm.content,
      plantForm.attention || undefined,
      plantForm.species || 'systems noticing',
      plantingCell,
      {
        repair_readiness: plantForm.repair_readiness,
        intention: plantForm.intention || undefined,
        impact: plantForm.impact || undefined,
        accountability: plantForm.accountability || undefined,
        followups: cleanFollowups.length ? cleanFollowups : undefined,
        emotion_tags: plantForm.emotion_tags.length ? plantForm.emotion_tags : undefined,
      }
    );
    setState(getCurrentTray());
    setPlantingCell(null);
    setActiveBench(s);
    // Record a wheel-relevant event locally (real AE write happens on live worker)
    toast.success('Seedling planted. The wheel and lens now see it.');
  }

  function addFollowupRow() {
    setPlantForm(f => ({ ...f, followups: [...f.followups, { actor: '', action: '', timeframe: '' }] }));
  }

  function updateFollowup(i: number, key: 'actor' | 'action' | 'timeframe', val: string) {
    setPlantForm(f => {
      const next = [...f.followups];
      next[i] = { ...next[i], [key]: val };
      return { ...f, followups: next };
    });
  }

  function toggleEmotionTag(tag: string) {
    setPlantForm(f => {
      const has = f.emotion_tags.includes(tag);
      return {
        ...f,
        emotion_tags: has ? f.emotion_tags.filter(t => t !== tag) : [...f.emotion_tags, tag].slice(0, 3),
      };
    });
  }

  function openTend(sdlg: Seedling) {
    setActiveBench(sdlg);
  }

  function closeBench() {
    setActiveBench(null);
    setState(getCurrentTray());
  }

  function afterBenchChange() {
    const fresh = getCurrentTray();
    if (fresh) setState({ ...fresh });
  }

  const visibleSeedlings = React.useMemo(() => {
    if (!state) return [];
    let list = state.seedlings.filter(s => !s.retracted_at);
    if (wheelFilter && list.length) {
      list = list.filter(s => {
        const tags = s.emotion_tags && s.emotion_tags.length ? s.emotion_tags : [];
        return tags.includes(wheelFilter);
      });
    }
    return list;
  }, [state, wheelFilter]);

  async function handleAttach(file: File) {
    if (!activeBench || !state) return;
    // In full live mode this would hit R2 via worker. Here we do a beautiful local simulation.
    const url = URL.createObjectURL(file);
    // attach to the seedling in local state
    (activeBench as any).attachment = { id: generateId(), seedling_id: activeBench.id, r2_key: url, mime: file.type, uploaded_at: Date.now() };
    const fresh = getCurrentTray();
    if (fresh) setState({ ...fresh });
    toast.success('Photo attached (local demo). In a live tray this goes to R2.');
  }

  // --- Buildable artifacts: Print the spread + seed packets ---

  function printSpread() {
    const w = window.open('', '_blank');
    if (!w || !state) return;
    const pressed = state.tendings.filter(t => t.kind === 'press');
    const html = `<!doctype html>
<html><head><title>What grew in ${state.tray.name || 'the tray'}</title>
<style>
body{font-family:Georgia,serif;background:#FBF7EF;color:#2C2522;padding:32px;line-height:1.45}
h1{font-size:22px;margin:0 0 6px} .meta{font-size:11px;color:#8A7F72;margin-bottom:18px}
.card{border:1px solid #C9BBA8;background:#fff;padding:14px 16px;margin-bottom:14px;max-width:460px;border-radius:6px}
.species{font-size:11px;letter-spacing:1px;color:#8B5E3C}
</style></head><body>
<h1>What grew in the ${state.tray.name || 'our'} tray</h1>
<div class="meta">${new Date().toLocaleDateString()} • ${pressed.length} pressed interpretations • code ${state.tray.code}</div>
${pressed.map(p => {
  const s = state.seedlings.find(x => x.id === p.seedling_id);
  const m = state.members.find(x => x.id === p.member_id);
  return `<div class="card"><div class="species">${s?.species || ''}</div><div>${p.content}</div><div style="margin-top:8px;font-size:11px">— ${m?.tendril_name || 'someone'}</div></div>`;
}).join('')}
<div style="margin-top:42px;font-size:10px;color:#8A7F72">Printed from Courage Tray • nothing leaves except what we choose to print</div>
</body></html>`;
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 240);
  }

  function exportSeedPackets() {
    if (!state) return;
    // Simple beautiful text bundle as .txt (real PNGs would use canvas; this is fully functional and printable)
    const lines: string[] = [];
    lines.push(`Courage Tray — seed packets for ${state.tray.name || state.tray.code}`);
    lines.push(`Printed ${new Date().toISOString().slice(0, 10)}`);
    lines.push('');
    state.seedlings.forEach(s => {
      const m = state.members.find(mm => mm.id === s.planter_member_id);
      lines.push(`— ${s.species} —`);
      lines.push(s.content);
      if (s.attention_source) lines.push(`(noticed ${s.attention_source})`);
      lines.push(`for ${m?.tendril_name || 'the circle'}`);
      lines.push('');
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    saveAs(blob, `seed-packets-${state.tray.code}.txt`);
    toast.success('Seed packet text bundle downloaded. Print, cut, mail.');
  }

  function switchTray(ref: any) {
    const loaded = getCurrentTray();
    if (loaded && loaded.tray.id === ref.trayId) {
      openTray(loaded);
      return;
    }
    // load from local mirror
    const s = joinTrayLocal(ref.code);
    openTray(s);
  }

  function leaveTray() {
    if (!state) return;
    if (!confirm('Leave this tray on this device? (You can rejoin with the code later)')) return;
    forgetTray(state.tray.id);
    setState(null);
    setView('landing');
    refreshMy();
  }

  // Keyboard delight
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key.toLowerCase() === 's' && (e.metaKey || e.ctrlKey) && view === 'tray') {
        e.preventDefault();
        setTab(t => t === 'spread' ? 'grid' : 'spread');
      }
      if (e.key === '?' && view === 'tray') {
        e.preventDefault();
        setTab('ledger');
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [view]);

  // --- LANDING ---
  if (view === 'landing') {
    return (
      <div className="min-h-[100svh] bg-[var(--bg)] text-[var(--text)]">
        <div className="max-w-[860px] mx-auto px-5 pt-12 pb-20">
          <div className="flex items-center justify-between mb-8">
            <div className="font-medium tracking-tight text-xl">Courage Tray</div>
            <CreatorLinks variant="compact" />
          </div>

          <div className="max-w-2xl">
            <div className="uppercase tracking-[3px] text-xs text-[var(--accent)] mb-2">FOR COMMUNITIES PRESERVING MEMORY AFTER TRANSITIONS</div>
            <h1 className="text-6xl sm:text-7xl font-medium tracking-[-2.6px] leading-[.9] mb-5">
              Scattered wisdom.<br />Systems thinking, held.
            </h1>
            <p className="text-2xl text-[var(--text-muted)] max-w-[30ch]">
              A courage seedling tray instead of another retro that no one reads again.
            </p>
          </div>

          <div className="mt-10 max-w-prose text-[15px] leading-relaxed text-[var(--text-muted)]">
            After a project ends, a collective dissolves, a move, a layoff, a campaign, a death — fragments of real systems insight scatter.
            This tray makes them plantable, tendable, and visible as a shared cognitive asset. No wellness. No signals.
            The emotion granularity wheel and the four recorded fields (repair readiness, intention/impact, accountability specificity, follow-ups) are the point.
          </div>

          <div className="mt-9 flex flex-wrap gap-3">
            <button onClick={() => { setView('tray'); const s = createTrayLocal(); openTray(s); }} className="group inline-flex items-center gap-3 rounded-2xl bg-[var(--accent)] text-white px-7 py-3.5 text-[15px] font-medium hover:brightness-105 active:brightness-95">
              Create a tray for your people <ArrowRight className="group-hover:translate-x-0.5 transition" />
            </button>
            <button onClick={() => { setView('tray'); setTab('grid'); }} className="inline-flex items-center gap-3 rounded-2xl border px-7 py-3.5 text-[15px] border-[var(--border)] hover:bg-[var(--bg-elev)]">
              Open an existing tray
            </button>
          </div>

          <div className="mt-16 grid md:grid-cols-2 gap-x-8 gap-y-9 text-sm">
            <div>
              <div className="font-medium mb-1.5">The courage seedling tray</div>
              <div className="text-[var(--text-muted)]">16 cells. Each seedling is one fragment of systems wisdom after a transition. It carries repair readiness, intention/impact separation, specific accountability, and concrete follow-ups.</div>
            </div>
            <div>
              <div className="font-medium mb-1.5">Emotion granularity wheel</div>
              <div className="text-[var(--text-muted)]">24 non-cliché textures (Recursive ache, Muffled rupture, Cost that surfaced late…). Click any petal to filter the tray. Sized by presence + reported readiness to act.</div>
            </div>
            <div>
              <div className="font-medium mb-1.5">Cognition Lens (limitations visible)</div>
              <div className="text-[var(--text-muted)]">A permanent panel that shows exactly what the tray recorded vs what any derived view can actually claim. Includes a fully local crude pattern detector with heavy disclaimers.</div>
            </div>
            <div>
              <div className="font-medium mb-1.5">The ledger + exports</div>
              <div className="text-[var(--text-muted)]">Everything that was asked of the community is visible. Full JSON + printable artifacts. The cognition asset belongs to the people who planted it.</div>
            </div>
          </div>

          <div className="mt-14">
            <CreatorLinks variant="hero" />
          </div>

          <div className="mt-12 text-[10px] text-[var(--text-subtle)] max-w-prose">
            Built on Cloudflare free tier: Pages (frontend), Workers (APIs), D1 (structured events), KV (flags/session), Analytics Engine (aggregates). Local demo is complete and rich. The wheel, lens, and all four cognition fields work immediately.
          </div>
        </div>
      </div>
    );
  }

  // --- TRAY EXPERIENCE ---
  if (!state) return null;

  const presenceCount = Math.max(1, Math.floor(state.members.length * 0.6) + (liveConnected ? 1 : 0));

  return (
    <div className="min-h-[100svh] bg-[var(--bg)] text-[var(--text)]">
      <div className="max-w-[1080px] mx-auto px-4 pt-5 pb-16">
        {/* tray chrome */}
        <div className="flex items-center justify-between mb-4 no-print">
          <div className="flex items-center gap-3">
            <div className="font-medium tracking-tight text-xl cursor-pointer" onClick={() => { setView('landing'); setState(null); }}>Courage Tray</div>
            <div className="text-xs px-2 py-px rounded bg-[var(--bg-elev)] border border-[var(--border)] text-[var(--text-subtle)]">{state.tray.code}</div>
            {state.tray.name && <div className="text-sm text-[var(--text-muted)]">• {state.tray.name}</div>}
          </div>

          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1 text-[var(--text-subtle)] text-xs">
              <Users size={14} /> {presenceCount} tendrils here
            </div>
            <button onClick={() => setTab('grid')} className={`inline-flex items-center gap-1.5 ${tab === 'grid' ? 'text-[var(--text)]' : 'text-[var(--text-muted)] hover:text-[var(--text)]'}`}><Leaf size={15} /> tray</button>
            <button onClick={() => setTab('wheel')} className={`inline-flex items-center gap-1.5 ${tab === 'wheel' ? 'text-[var(--text)]' : 'text-[var(--text-muted)] hover:text-[var(--text)]'}`}><Eye size={15} /> wheel</button>
            <button onClick={() => setTab('spread')} className={`inline-flex items-center gap-1.5 ${tab === 'spread' ? 'text-[var(--text)]' : 'text-[var(--text-muted)] hover:text-[var(--text)]'}`}><BookOpen size={15} /> spread</button>
            <button onClick={() => setTab('ledger')} className={`inline-flex items-center gap-1.5 ${tab === 'ledger' ? 'text-[var(--text)]' : 'text-[var(--text-muted)] hover:text-[var(--text)]'}`}><ScrollText size={15} /> ledger</button>

            <button onClick={printSpread} className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-xl border border-[var(--border)] hover:bg-[var(--bg-elev)]"><Printer size={14} /> print</button>
            <button onClick={exportSeedPackets} className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-xl border border-[var(--border)] hover:bg-[var(--bg-elev)]"><Download size={14} /> export</button>

            <button onClick={leaveTray} className="text-xs text-[var(--text-subtle)] hover:text-[var(--text)]">leave</button>
            <CreatorLinks variant="tray" />
          </div>
        </div>

        {/* my trays switcher */}
        {myTrays.length > 1 && (
          <div className="flex gap-2 flex-wrap mb-4 text-xs no-print">
            {myTrays.map((ref, i) => (
              <button key={i} onClick={() => switchTray(ref)} className="px-2.5 py-px rounded border border-[var(--border)] hover:bg-[var(--bg-elev)]">
                {ref.code}
              </button>
            ))}
            <button onClick={() => { setView('landing'); }} className="px-2.5 py-px rounded border border-[var(--border)] hover:bg-[var(--bg-elev)] flex items-center gap-1"><Plus size={12} /> new</button>
          </div>
        )}

        {/* quick create / join bar when on tray */}
        <div className="mb-5 flex flex-wrap gap-2 text-sm no-print">
          <input value={trayName} onChange={e => setTrayName(e.target.value)} placeholder="name this circle (optional)" className="rounded-xl border px-3 py-1 text-sm bg-transparent border-[var(--border)]" />
          <button onClick={createLive} className="rounded-xl border px-4 py-1 text-sm hover:bg-[var(--bg-elev)]">create live tray</button>

          <input value={joinCode} onChange={e => setJoinCode(e.target.value.toUpperCase())} placeholder="JOIN CODE" className="rounded-xl border px-3 py-1 text-sm font-mono bg-transparent border-[var(--border)] w-28" />
          <input value={newTendril} onChange={e => setNewTendril(e.target.value)} placeholder="your tendril name" className="rounded-xl border px-3 py-1 text-sm bg-transparent border-[var(--border)]" />
          <button onClick={joinLive} className="rounded-xl border px-4 py-1 text-sm hover:bg-[var(--bg-elev)]">join</button>
        </div>

        {/* main surface */}
        {tab === 'grid' && (
          <div>
            <div className="mb-3 text-xs text-[var(--text-subtle)] flex items-center gap-2">
              {wheelFilter ? <span>Filtered by <span className="font-medium text-[var(--text)]">{wheelFilter}</span> — </span> : null}
              {visibleSeedlings.length} seedlings shown. Click a sprout to open the tending bench or plant in an empty cell.
            </div>
            <TrayGrid state={{ ...state, seedlings: visibleSeedlings }} onPlant={openPlant} onTend={openTend} />
          </div>
        )}

        {tab === 'wheel' && (
          <div className="max-w-[620px]">
            <EmotionWheel
              seedlings={state.seedlings.filter(s => !s.retracted_at)}
              onSegmentClick={(emo) => {
                setWheelFilter(emo);
                setTab('grid');
              }}
              activeFilter={wheelFilter}
            />
            <div className="mt-6">
              <CognitionLens state={state} onFilter={(emo) => { setWheelFilter(emo); setTab('grid'); }} />
            </div>
          </div>
        )}

        {tab === 'spread' && (
          <div>
            <div className="mb-2 text-sm flex items-center justify-between">
              <div>The social interpretation spread — drag the pressed readings. Ambiguity is allowed to stay.</div>
              <button onClick={printSpread} className="text-xs underline">print this spread</button>
            </div>
            <SpreadCanvas state={state} onUpdate={() => setState(getCurrentTray())} />
          </div>
        )}

        {tab === 'ledger' && (
          <div className="max-w-2xl space-y-6">
            <div className="mb-1 text-sm">The ledger makes visible what the tray has asked of people and that nothing is extracted for any other purpose. This is the cognition asset in raw form.</div>
            <LedgerPanel state={state} onChange={() => setState(getCurrentTray())} />
            <CognitionLens state={state} onFilter={(emo) => { setWheelFilter(emo); setTab('grid'); }} />
          </div>
        )}

        {/* presence + slow wave hint */}
        <div className="mt-6 text-[10px] text-[var(--text-subtle)] flex items-center gap-4 no-print">
          <div>Code <span className="font-mono text-[var(--text-muted)]">{state.tray.code}</span> — share this with the circle. Plant systems wisdom, not signals.</div>
        </div>

        <div className="mt-8">
          <CreatorLinks variant="footer" />
        </div>
      </div>

      {/* Rich structured plant form — captures repair readiness, intention-impact, accountability specificity, follow-ups, emotion tags */}
      {plantingCell !== null && (
        <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center bg-black/30 p-3" onClick={() => setPlantingCell(null)}>
          <div className="bg-[var(--bg-elev)] border border-[var(--border)] rounded-3xl w-full max-w-[720px] shadow-xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-[var(--border)] flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[1.5px] text-[var(--text-subtle)]">planting in cell {plantingCell + 1}</div>
                <div className="font-medium">Capture a fragment of systems wisdom</div>
              </div>
              <button onClick={() => setPlantingCell(null)}><X size={19} /></button>
            </div>

            <div className="p-5 space-y-4 text-sm max-h-[72vh] overflow-auto">
              <textarea
                value={plantForm.content}
                onChange={e => setPlantForm(f => ({ ...f, content: e.target.value }))}
                placeholder="One specific thing that became visible only after the transition. What loop, cost, boundary, or leverage point surfaced too late or too quietly?"
                className="w-full min-h-[92px] rounded-2xl border border-[var(--border)] p-3 bg-transparent text-[14px]"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input value={plantForm.attention} onChange={e => setPlantForm(f => ({ ...f, attention: e.target.value }))} placeholder="Where was your attention when this surfaced? (grounds the cognition)" className="rounded-2xl border px-3 py-2 bg-transparent text-sm" />
                <input value={plantForm.species} onChange={e => setPlantForm(f => ({ ...f, species: e.target.value }))} placeholder="Short label (e.g. missed loop, invisible cost)" className="rounded-2xl border px-3 py-2 bg-transparent text-sm" />
              </div>

              {/* Repair readiness */}
              <div>
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <div>Repair readiness — how ready does the system feel to act on this? <span className="font-mono tabular-nums">{plantForm.repair_readiness}</span></div>
                </div>
                <input type="range" min={0} max={100} step={1} value={plantForm.repair_readiness} onChange={e => setPlantForm(f => ({ ...f, repair_readiness: parseInt(e.target.value) }))} className="w-full accent-[var(--accent)]" />
                <div className="text-[10px] text-[var(--text-subtle)] mt-0.5">This number is part of the asset. It is not a target to optimize.</div>
              </div>

              {/* Intention / Impact separation */}
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-[var(--text-subtle)] mb-1">Intention (what was meant / hoped)</div>
                  <textarea value={plantForm.intention} onChange={e => setPlantForm(f => ({ ...f, intention: e.target.value }))} className="w-full h-16 rounded-2xl border p-2 text-sm bg-transparent" placeholder="We wanted to protect the team from churn..." />
                </div>
                <div>
                  <div className="text-xs text-[var(--text-subtle)] mb-1">Impact (what actually landed / observed)</div>
                  <textarea value={plantForm.impact} onChange={e => setPlantForm(f => ({ ...f, impact: e.target.value }))} className="w-full h-16 rounded-2xl border p-2 text-sm bg-transparent" placeholder="The people who saw risk earliest lost all line of sight..." />
                </div>
              </div>

              {/* Accountability specificity */}
              <div>
                <div className="text-xs text-[var(--text-subtle)] mb-1">Accountability specificity — who exactly can do what observable thing?</div>
                <input value={plantForm.accountability} onChange={e => setPlantForm(f => ({ ...f, accountability: e.target.value }))} placeholder="The VP of Delivery can reinstate the old risk huddle by Friday memo" className="w-full rounded-2xl border px-3 py-2 text-sm bg-transparent" />
                <div className="text-[10px] text-[var(--text-subtle)] mt-1">Vague language (“someone should follow up”) is visible as a gap in the lens.</div>
              </div>

              {/* Follow-ups */}
              <div>
                <div className="text-xs text-[var(--text-subtle)] mb-1.5">Concrete follow-ups (actor + action + timeframe)</div>
                {plantForm.followups.map((f, i) => (
                  <div key={i} className="grid grid-cols-3 gap-2 mb-2">
                    <input value={f.actor} onChange={e => updateFollowup(i, 'actor', e.target.value)} placeholder="who" className="rounded-xl border px-2 py-1.5 text-xs bg-transparent" />
                    <input value={f.action} onChange={e => updateFollowup(i, 'action', e.target.value)} placeholder="exact action" className="rounded-xl border px-2 py-1.5 text-xs bg-transparent col-span-1" />
                    <input value={f.timeframe} onChange={e => updateFollowup(i, 'timeframe', e.target.value)} placeholder="by when" className="rounded-xl border px-2 py-1.5 text-xs bg-transparent" />
                  </div>
                ))}
                <button onClick={addFollowupRow} className="text-xs underline">+ another follow-up</button>
              </div>

              {/* Emotion granularity picker */}
              <div>
                <div className="text-xs text-[var(--text-subtle)] mb-1.5">Tag with 1–3 granular emotions (these power the wheel)</div>
                <div className="flex flex-wrap gap-1.5">
                  {GRANULAR_EMOTIONS.slice(0, 18).map(em => {
                    const active = plantForm.emotion_tags.includes(em);
                    return (
                      <button key={em} onClick={() => toggleEmotionTag(em)} className={`text-xs px-2.5 py-1 rounded-full border ${active ? 'bg-[var(--accent)] text-white border-[var(--accent)]' : 'border-[var(--border)] hover:bg-[var(--bg)]'}`}>
                        {em}
                      </button>
                    );
                  })}
                </div>
                <div className="text-[10px] text-[var(--text-subtle)] mt-1">Click the wheel tab later to see how these textures aggregate across the tray.</div>
              </div>
            </div>

            <div className="px-5 py-3 border-t border-[var(--border)] bg-[var(--bg)] flex items-center justify-between text-xs">
              <div className="text-[var(--text-subtle)]">This seedling (and everything added to it) stays inside this tray. Export the full JSON anytime.</div>
              <div className="flex gap-2">
                <button onClick={() => setPlantingCell(null)} className="rounded-2xl border px-5 py-2">cancel</button>
                <button onClick={submitPlant} className="rounded-2xl bg-[var(--accent)] text-white px-5 py-2">plant seedling</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* the heart of the interaction */}
      {activeBench && state && (
        <TendingBench
          seedling={activeBench}
          state={state}
          onClose={closeBench}
          onChange={afterBenchChange}
          onAttach={handleAttach}
        />
      )}

      <Toaster position="top-center" richColors closeButton />
    </div>
  );
}
